/**
 * Contextual "what can you do now" actions for the house dashboard. Every entry is derived from the same
 * conditions the sim's command validators apply (see the comments), so each call to action can really be done.
 */
import { AGE_OF_MAJORITY, BUILDING_DEFS, EDUCATION_START_AGE, RESOURCE_KEYS, SQUIRE_MAX_START_AGE, SQUIRE_START_AGE, SQUIRE_XP_MAX, TRAIT_DEFS } from '@pk/shared';
import type { BuildingKey, CharacterView, HouseView, Id, Resources, WorldView } from '@pk/shared';
import { memberIds } from '../api/hooks';

export type ActionIcon = 'perk' | 'proposal' | 'letter' | 'war' | 'family' | 'child' | 'knight' | 'petition' | 'build' | 'army' | 'plot';

export interface HouseAction {
  key: string;
  /** Lower comes first. */
  prio: number;
  icon: ActionIcon;
  tone: 'gold' | 'red' | 'blue' | 'green';
  /** Route to open. */
  to: string;
  label: { key: string; params?: Record<string, string | number> };
  hint: { key: string; params?: Record<string, string | number> };
  /** Optional pre-formatted extra text (costs), appended to the hint by the view. */
  extra?: string;
}

const DEBT_FLOOR = -200; // sim/economy.ts
const BUILD_PREFERENCE: BuildingKey[] = ['market', 'farmland', 'barracks', 'walls', 'lumber_camp', 'quarry', 'mine', 'stables'];

const hasEduTrait = (c: CharacterView) => c.traits.some((k) => TRAIT_DEFS[k]?.category === 'education');

/** Cost text for the hint line, e.g. "70 gold, 15 timber". */
function costLine(cost: Partial<Resources>, mult: number): string {
  return (Object.keys(cost) as (keyof Resources)[]).filter((k) => (cost[k] ?? 0) > 0).map((k) => `${(cost[k] ?? 0) * mult} ${k}`).join(', ');
}

/** People with a marriage / betrothal or service proposal still on its way (those cannot start another). */
export function busyMembers(h: HouseView): { marriage: Set<Id>; service: Set<Id>; squire: Set<Id> } {
  const marriage = new Set<Id>();
  const service = new Set<Id>();
  const squire = new Set<Id>();
  for (const p of [...h.outgoingProposals, ...h.incomingProposals]) {
    if (p.status !== 'pending') continue;
    if (p.kind === 'marriage' || p.kind === 'betrothal') { if (p.subjectAId) marriage.add(p.subjectAId); if (p.subjectBId) marriage.add(p.subjectBId); }
    if (p.kind === 'service' && p.subjectAId) service.add(p.subjectAId);
    if (p.kind === 'squire' && p.subjectAId) squire.add(p.subjectAId);
  }
  return { marriage, service, squire };
}

export type MemberAct = 'edu' | 'marry' | 'service' | 'petition' | 'squire';

/**
 * Landless, undubbed, non-head and free of prison, and either aged 12..21 with no training yet (a late squire) or
 * already in training (xp < 100) at any age: may be sent to train as a squire. Mirrors sim squires.squireEligible.
 */
export function squireEligible(c: CharacterView, h: HouseView): boolean {
  if (c.squireXp !== null ? c.squireXp >= SQUIRE_XP_MAX : c.age > SQUIRE_MAX_START_AGE) return false;
  return c.alive && c.age >= SQUIRE_START_AGE && c.heldTitleIds.length === 0 && c.id !== h.house.headId && !c.dubbed && !c.knightOf && !c.prisonerOf;
}

/** Which of the family actions make sense for this living member right now (same rules as the dashboard suggestions). */
export function memberCan(c: CharacterView, h: HouseView, w: WorldView, busy = busyMembers(h)): Record<MemberAct, boolean> {
  const mine = memberIds(h);
  const adult = c.age >= AGE_OF_MAJORITY;
  const served = c.knightOf ? w.titles.find((x) => x.id === c.knightOf) : undefined;
  return {
    edu: c.alive && c.age >= EDUCATION_START_AGE && c.age < AGE_OF_MAJORITY && !c.education && !hasEduTrait(c),
    marry: c.alive && adult && !c.spouseId && !c.betrothedId && !busy.marriage.has(c.id) && !c.prisonerOf,
    // only a dubbed knight can enter service (an undubbed member trains as a squire first)
    service: c.alive && adult && c.dubbed && c.age < 45 && !c.knightOf && !c.squireOf && c.squireXp === null && c.heldTitleIds.length === 0 && c.id !== h.house.headId
      && !c.spouseId && !c.betrothedId && !c.prisonerOf && !busy.service.has(c.id),
    petition: c.alive && adult && !!served?.holderId && !mine.has(served.holderId) && !c.prisonerOf,
    // In training without a placement, or an untrained member aged 12..21 (a late squire included).
    squire: squireEligible(c, h) && !c.squireOf && !busy.squire.has(c.id),
  };
}

export function houseActions(h: HouseView, w: WorldView): HouseAction[] {
  const out: HouseAction[] = [];
  const mine = memberIds(h);
  const ourHouse = h.house.id;
  const day = w.day;
  const titleOf = (id: Id | null) => (id ? w.titles.find((x) => x.id === id) : undefined);
  const living = h.members.filter((m) => m.alive);
  const headId = h.house.headId;

  // --- proposals awaiting an answer (answer_proposal): answered in the Court
  const pending = h.incomingProposals.filter((p) => p.status === 'pending');
  if (pending.length > 0) {
    out.push({ key: 'proposals', prio: 10, icon: 'proposal', tone: 'gold', to: '/court',
      label: { key: 'house.todo.proposals', params: { count: pending.length } }, hint: { key: 'house.todo.proposalsHint' } });
  }

  // --- plots against us / our discovered schemes: shown in the Court
  const plots = h.plotsAgainstUs.length;
  if (plots > 0) {
    out.push({ key: 'plots', prio: 12, icon: 'plot', tone: 'red', to: '/court',
      label: { key: 'house.todo.plots', params: { count: plots } }, hint: { key: 'house.todo.plotsHint' } });
  }
  const discovered = h.schemes.filter((x) => x.discovered).length;
  if (discovered > 0) {
    out.push({ key: 'discovered', prio: 13, icon: 'plot', tone: 'red', to: '/court',
      label: { key: 'house.todo.discovered', params: { count: discovered } }, hint: { key: 'house.todo.discoveredHint' } });
  }

  // --- peace in wars we lead (offer_peace: sender must be a house member leading a side; one offer at a time)
  for (const war of h.wars) {
    if (war.endedDay !== null) continue;
    if (!mine.has(war.attackerId) && !mine.has(war.defenderId)) continue;
    if (h.outgoingProposals.some((p) => p.kind === 'peace' && p.warId === war.id && p.status === 'pending')) continue;
    const iAttack = mine.has(war.attackerId);
    const score = Math.round(iAttack ? war.score : -war.score);
    out.push({ key: `peace-${war.id}`, prio: 15, icon: 'war', tone: score < 0 ? 'red' : 'blue', to: '/military',
      label: { key: 'house.todo.peace', params: { name: war.name } },
      hint: { key: score < 0 ? 'house.todo.peaceLosing' : 'house.todo.peaceHint', params: { score: `${score > 0 ? '+' : ''}${score}` } } });
  }

  // --- unread letters that have arrived
  const unread = h.letters.filter((l) => !l.read && mine.has(l.toId) && l.arriveDay <= day).length;
  if (unread > 0) {
    out.push({ key: 'letters', prio: 20, icon: 'letter', tone: 'blue', to: '/court',
      label: { key: 'house.todo.letters', params: { count: unread } }, hint: { key: 'house.todo.lettersHint' } });
  }

  // --- raise levies (raise_army: the head must hold holdings with a levy pool, and own no army yet)
  const actor = headId ?? living[0]?.id ?? '';
  const haveArmy = h.armies.some((a) => a.ownerId === actor);
  const levies = w.titles.filter((x) => x.holding && x.holderHouseId === ourHouse && !x.holding.occupiedBy)
    .reduce((s, x) => s + x.holding!.levyPool, 0);
  const headHolds = w.titles.some((x) => x.holding && x.holderId === actor && !x.holding.occupiedBy && x.holding.levyPool > 0);
  const atWar = h.wars.some((x) => x.endedDay === null);
  if (!haveArmy && headHolds && levies > 0) {
    out.push({ key: 'levies', prio: atWar ? 25 : 70, icon: 'army', tone: atWar ? 'red' : 'blue', to: '/military',
      label: { key: 'house.todo.levies', params: { n: levies } }, hint: { key: atWar ? 'house.todo.leviesWar' : 'house.todo.leviesHint' } });
  }

  // --- family: education, marriage, knights, petitions
  const busy = busyMembers(h);
  const can = new Map(living.map((m) => [m.id, memberCan(m, h, w, busy)] as const));

  // set_education: age 6..15, not yet educated
  for (const c of living.filter((m) => can.get(m.id)!.edu).sort((a, b) => b.age - a.age).slice(0, 2)) {
    out.push({ key: `edu-${c.id}`, prio: 30, icon: 'child', tone: 'green', to: `/character/${c.id}?act=edu`,
      label: { key: 'house.todo.education', params: { name: c.firstName, age: c.age } }, hint: { key: 'house.todo.educationHint' } });
  }

  // choose_perk: a skill reached training rank 3, 6 or 9 and no perk was picked yet
  for (const c of living.filter((m) => (m.pendingPerks?.length ?? 0) > 0).slice(0, 3)) {
    out.push({ key: `perk-${c.id}`, prio: 26, icon: 'perk', tone: 'gold', to: `/character/${c.id}`,
      label: { key: 'house.todo.perk', params: { name: c.firstName, count: c.pendingPerks!.length } }, hint: { key: 'house.todo.perkHint' } });
  }

  // propose_marriage: adult, unmarried, not betrothed, no proposal on its way
  for (const c of living.filter((m) => can.get(m.id)!.marry).sort((a, b) => a.age - b.age).slice(0, 2)) {
    out.push({ key: `marry-${c.id}`, prio: 32, icon: 'family', tone: 'gold', to: `/character/${c.id}?act=marry`,
      label: { key: 'house.todo.marry', params: { name: c.firstName, age: c.age } }, hint: { key: 'house.todo.marryHint' } });
  }

  // petition_liege: a sworn adult knight whose lord belongs to another house
  for (const c of living.filter((m) => can.get(m.id)!.petition).slice(0, 2)) {
    const served = titleOf(c.knightOf)!;
    out.push({ key: `petition-${c.id}`, prio: 40, icon: 'petition', tone: 'gold', to: `/character/${c.id}?act=petition`,
      label: { key: 'house.todo.petition', params: { name: c.firstName } },
      hint: { key: 'house.todo.petitionHint', params: { title: served.name } } });
  }

  // build: a holding of ours with nothing under construction and an affordable, allowed, not-maxed building
  const gold = h.house.resources.gold;
  if (gold >= DEBT_FLOOR) {
    let best: { score: number; action: HouseAction } | null = null;
    for (const t of w.titles) {
      const hd = t.holding;
      if (!hd || !t.holderId || !mine.has(t.holderId) || hd.occupiedBy || hd.construction) continue;
      for (const k of BUILD_PREFERENCE) {
        const def = BUILDING_DEFS[k];
        const lvl = hd.buildings[k] ?? 0;
        if (lvl >= def.maxLevel) continue;
        if (def.allowedTerrain !== 'any' && !def.allowedTerrain.includes(hd.terrain)) continue;
        const target = lvl + 1;
        const affordable = RESOURCE_KEYS.every((r) => h.house.resources[r] >= ((def.cost as Partial<Resources>)[r] ?? 0) * target);
        if (!affordable) continue;
        const score = BUILD_PREFERENCE.indexOf(k) * 10 + lvl;
        if (!best || score < best.score) {
          best = { score, action: {
            key: `build-${t.id}`, prio: 45, icon: 'build', tone: 'green', to: `/title/${t.id}?tab=holding`,
            label: { key: lvl === 0 ? 'house.todo.build' : 'house.todo.upgrade', params: { building: def.name, place: t.name, level: target } },
            hint: { key: 'house.todo.buildHint', params: { cost: costLine(def.cost as Partial<Resources>, target) } },
          } };
        }
        break;
      }
    }
    if (best) out.push(best.action);
  }

  // offer_service: unmarried dubbed adult without land who is not the head
  for (const c of living.filter((m) => can.get(m.id)!.service).sort((a, b) => a.age - b.age).slice(0, 1)) {
    out.push({ key: `knight-${c.id}`, prio: 50, icon: 'knight', tone: 'blue', to: `/character/${c.id}?act=service`,
      label: { key: 'house.todo.knight', params: { name: c.firstName } }, hint: { key: 'house.todo.knightHint' } });
  }

  // send_squire / join_guard: an unplaced squire (in training) or an untrained member aged 12-21 with no placement
  for (const c of living.filter((m) => can.get(m.id)!.squire).sort((a, b) => Number(b.squireXp !== null) - Number(a.squireXp !== null) || a.age - b.age).slice(0, 1)) {
    out.push({ key: `squire-${c.id}`, prio: 52, icon: 'knight', tone: 'blue', to: `/character/${c.id}?act=squire`,
      label: { key: 'house.todo.squire', params: { name: c.firstName, age: c.age } }, hint: { key: 'house.todo.squireHint' } });
  }

  return out.sort((a, b) => a.prio - b.prio);
}
